import React from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Calendar } from "../ui/calendar";

function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}
function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday-start
  x.setDate(x.getDate() - day);
  return x;
}

export function DayNavigator({ value, onChange }) {
  const d = new Date(value + "T12:00:00");
  const shift = (n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    onChange(fmtDate(x));
  };
  const label = d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  return (
    <div className="flex items-center gap-2" data-testid="day-navigator">
      <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="day-prev">
        <ChevronLeft size={14} />
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="min-w-[220px] justify-start gap-2" data-testid="day-picker">
            <CalendarDays size={14} />
            <span className="font-mono-rw text-xs">{label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-auto bg-white">
          <Calendar mode="single" selected={d} onSelect={(x) => x && onChange(fmtDate(x))} />
        </PopoverContent>
      </Popover>
      <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="day-next">
        <ChevronRight size={14} />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onChange(fmtDate(new Date()))} data-testid="day-today">
        Today
      </Button>
    </div>
  );
}

export function MonthNavigator({ value, onChange }) {
  // value format: "YYYY-MM"
  const [y, m] = value.split("-").map(Number);
  const shift = (n) => {
    const d = new Date(y, m - 1 + n, 1);
    onChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const label = new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return (
    <div className="flex items-center gap-2" data-testid="month-navigator">
      <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="month-prev">
        <ChevronLeft size={14} />
      </Button>
      <div className="min-w-[180px] px-3 py-1.5 border border-[var(--rw-border)] rounded-[4px] font-mono-rw text-xs text-center" data-testid="month-label">
        {label}
      </div>
      <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="month-next">
        <ChevronRight size={14} />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => {
        const d = new Date();
        onChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
      }} data-testid="month-current">
        Current
      </Button>
    </div>
  );
}

export function WeekNavigator({ value, onChange }) {
  // value = ISO Monday-of-week "YYYY-MM-DD"
  const d = new Date(value + "T12:00:00");
  const end = new Date(d);
  end.setDate(d.getDate() + 6);
  const shift = (n) => {
    const x = new Date(d);
    x.setDate(d.getDate() + n * 7);
    onChange(fmtDate(startOfWeek(x)));
  };
  const fmt = (x) => x.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return (
    <div className="flex items-center gap-2" data-testid="week-navigator">
      <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="week-prev">
        <ChevronLeft size={14} />
      </Button>
      <div className="min-w-[220px] px-3 py-1.5 border border-[var(--rw-border)] rounded-[4px] font-mono-rw text-xs text-center">
        {fmt(d)} — {fmt(end)}, {end.getFullYear()}
      </div>
      <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="week-next">
        <ChevronRight size={14} />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onChange(fmtDate(startOfWeek(new Date())))} data-testid="week-current">
        This week
      </Button>
    </div>
  );
}

export { fmtDate, startOfWeek };

export function YearNavigator({ value, onChange }) {
  // value = "YYYY"
  const y = parseInt(value, 10) || new Date().getFullYear();
  const shift = (n) => onChange(String(y + n));
  return (
    <div className="flex items-center gap-2" data-testid="year-navigator">
      <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="year-prev">
        <ChevronLeft size={14} />
      </Button>
      <div
        className="min-w-[120px] px-3 py-1.5 border border-[var(--rw-border)] rounded-[4px] font-mono-rw text-xs text-center"
        data-testid="year-label"
      >
        {y}
      </div>
      <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="year-next">
        <ChevronRight size={14} />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange(String(new Date().getFullYear()))}
        data-testid="year-current"
      >
        This year
      </Button>
    </div>
  );
}
